import { useCallback } from 'react';

import { PartLocationObject } from '../../../@types/part.type';
import {
  useGetOnePartForService,
  useGetPartLocationDataByIdForService,
  useUpdatePartLocation,
} from '../../../queries/part-query';

export interface PartLocationUpdate {
  locationId: number;
  locationTypeCode: string;
  locationHierarchy: string;
}

export interface UseRepairJobPartResult {
  partSerial: string;
  partTypeName: string;
  partLocation?: PartLocationObject;
  isUpdatingLocation: boolean;
  handlePartLocationChange: (value: PartLocationUpdate) => Promise<void>;
}

export const useRepairJobPart = (partId: number | undefined): UseRepairJobPartResult => {
  const effectivePartId = partId && partId > 0 ? partId : 0;

  const partQuery = useGetOnePartForService(effectivePartId);
  const partLocationQuery = useGetPartLocationDataByIdForService(effectivePartId);
  const updatePartLocation = useUpdatePartLocation(effectivePartId);

  const partSerial = partQuery.data?.serial1 ?? '';
  const partTypeName = partQuery.data?.partType?.name ?? '';
  const partLocation = partLocationQuery.data ?? undefined;

  const { refetch: refetchPartLocation } = partLocationQuery;
  const handlePartLocationChange = useCallback(
    async (value: PartLocationUpdate) => {
      if (!effectivePartId) return;
      await updatePartLocation.mutateAsync({
        id: effectivePartId,
        locationId: value.locationId,
        locationTypeCode: value.locationTypeCode,
        locationHierarchy: value.locationHierarchy,
      });
      await refetchPartLocation();
    },
    [effectivePartId, updatePartLocation, refetchPartLocation]
  );

  return {
    partSerial,
    partTypeName,
    partLocation,
    isUpdatingLocation: updatePartLocation.isPending,
    handlePartLocationChange,
  };
};
